import type { ReactNode } from "react";
import { Skeleton } from "@nasam/ui";

/** Icon+label-above-value cell, mirrors the `DetailField` pattern duplicated
 * across the organization detail screens. */
export function DetailFieldSkeleton({
  wide = false,
}: {
  readonly wide?: boolean;
}) {
  return (
    <div
      className={`flex w-full flex-col items-end gap-1.5 px-2 ${wide ? "" : "md:flex-1"}`}
    >
      <Skeleton className="h-3 w-20 rounded-full" />
      <Skeleton className={`h-4 rounded-full ${wide ? "w-40" : "w-28"}`} />
    </div>
  );
}

/** One or two fields side by side, mirrors `DetailRow`. */
export function DetailRowSkeleton({
  wide = false,
}: {
  readonly wide?: boolean;
}) {
  return (
    <div className="flex flex-col items-stretch gap-2 px-4 py-2 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4">
      {wide ? (
        <DetailFieldSkeleton wide />
      ) : (
        <>
          <DetailFieldSkeleton />
          <DetailFieldSkeleton />
        </>
      )}
    </div>
  );
}

/** Bordered card with a title bar and N field rows -- the section shape the
 * organization detail screens share (each row is a paired or full-width
 * field, per `DetailRow`'s two layouts). */
export function DetailSectionSkeleton({
  rows,
  children,
}: {
  readonly rows: ReadonlyArray<"pair" | "wide">;
  readonly children?: ReactNode;
}) {
  return (
    <div className="rounded-md border border-container-default-border bg-container-default-background py-2">
      <div className="flex justify-end px-4 pb-2">
        <Skeleton className="h-3 w-28 rounded-full" />
      </div>
      {rows.map((kind, index) => (
        <DetailRowSkeleton key={index} wide={kind === "wide"} />
      ))}
      {children}
    </div>
  );
}

// Lives next to PageHeader in @nasam/ui so the two stay in sync; re-exported
// here so existing screen imports keep working.
export { PageHeaderSkeleton } from "@nasam/ui";

/** Label + control, mirrors `FormField`/`FormLabel`/`Input`. */
export function FormFieldSkeleton({
  wide = false,
}: {
  readonly wide?: boolean;
}) {
  return (
    <div className={`flex flex-col gap-2 ${wide ? "md:col-span-2" : ""}`}>
      <Skeleton className="h-3 w-24 rounded-full" />
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}

/** The bordered grid every form screen lays its fields out in. */
export function FormSectionSkeleton({
  fields,
}: {
  readonly fields: ReadonlyArray<"regular" | "wide">;
}) {
  return (
    <div className="grid gap-4 rounded-md border border-container-default-border bg-container-default-background p-4 md:grid-cols-2">
      {fields.map((kind, index) => (
        <FormFieldSkeleton key={index} wide={kind === "wide"} />
      ))}
    </div>
  );
}

/** Replaces a stat badge's numeric value while its summary query loads --
 * same footprint as the real number so the badge doesn't reflow on arrival. */
export function StatValueSkeleton({ label }: { readonly label: string }) {
  return (
    <span className="inline-flex items-center">
      <span role="status" className="sr-only">
        {label}
      </span>
      <Skeleton aria-hidden="true" className="h-4 w-6 rounded-full" />
    </span>
  );
}

const CONTENT_ROW_WIDTHS = ["w-full", "w-11/12", "w-4/5", "w-3/4", "w-5/6"];

/** Generic body rows for places that can't know the real content shape --
 * e.g. the router's Suspense fallback, which covers every lazily-loaded
 * screen. Widths cycle so it reads as content rather than a wall of bars. */
export function ContentRowsSkeleton({
  rows = 4,
}: {
  readonly rows?: number;
}) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={index}
          className={`h-4 rounded-full ${CONTENT_ROW_WIDTHS[index % CONTENT_ROW_WIDTHS.length]}`}
        />
      ))}
    </div>
  );
}
